/**
 * 班级荣誉 store 模块
 */
import {
  getClassHonors,
  getClassHonor,
  getClassHonorsByClass,
  createClassHonor as apiCreate,
  updateClassHonor as apiUpdate,
  deleteClassHonor as apiDelete
} from '@/api/classHonors'

/** 后端 type: 0=文字, 1=图片 -> 前端 honorType: 'text'|'image' */
function typeToHonorType(t) {
  if (t === 1 || t === '1') return 'image'
  return 'text'
}

/** 将 API 返回项转为前端格式 */
function normalizeHonor(raw) {
  if (!raw) return null
  const type = raw.type ?? raw.honor_type ?? raw.honorType
  const honorType = typeToHonorType(type)
  const fileUrl = raw.file_url ?? raw.image_url ?? raw.imageUrl ?? raw.file_path ?? raw.image_path ?? raw.file ?? ''
  const classInfo = raw.class_info ?? {}
  const className = raw.class_name ?? raw.className ?? classInfo.class_name ?? ''
  let attachments = raw.attachments ?? []
  if (Array.isArray(attachments) && attachments.length) {
    attachments = attachments
      .map(a => typeof a === 'string' ? { url: a } : { url: a.url ?? a.path ?? a.file })
      .filter(a => a.url)
  } else if (fileUrl) {
    attachments = [{ url: fileUrl }]
  }
  return {
    id: raw.honor_id ?? raw.id,
    classId: raw.class_id ?? raw.classId ?? '',
    className,
    title: raw.title ?? raw.honor_title ?? '',
    description: raw.description ?? '',
    honorType,
    imageUrl: fileUrl || (attachments[0]?.url ?? attachments[0]),
    fileUrl,
    attachments,
    honorDate: raw.honor_date ?? raw.honorDate ?? raw.create_time ?? raw.created_at ?? '',
    createdAt: raw.create_time ?? raw.created_at ?? raw.createdAt ?? ''
  }
}

/** 后端未就绪时的演示数据 */
function getMockHonors() {
  return [
    { honor_id: 1, type: 0, class_id: 1, title: '文明班集体', description: '2025年第一学期文明班级', honor_date: '2025-01-01T00:00:00', class_info: { class_name: '高一(1)班', grade: '高一' } },
    { honor_id: 2, type: 0, class_id: 1, title: '优秀班集体', description: '2024-2025学年优秀班集体', honor_date: '2025-06-01T00:00:00', class_info: { class_name: '高一(1)班', grade: '高一' } },
    { honor_id: 3, type: 1, class_id: 1, title: '运动会团体第一', file_url: '/uploads/honors/sport.jpg', honor_date: '2025-10-01T00:00:00', class_info: { class_name: '高一(1)班', grade: '高一' } },
    { honor_id: 4, type: 1, class_id: 2, title: '卫生流动红旗', file_url: '/uploads/honors/health.jpg', honor_date: '2025-02-01T00:00:00', class_info: { class_name: '高一(2)班', grade: '高一' } }
  ]
}

/** 将日期格式化为后端 ISO 格式 YYYY-MM-DDTHH:mm:00 */
function toHonorDateStr(v) {
  const s = (v ?? '').toString().trim()
  if (!s) return ''
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(s)) return s
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(s)) return s + ':00'
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s + 'T00:00:00'
  if (/^\d{4}-\d{2}$/.test(s)) return s + '-01T00:00:00'
  return s
}

/** 转为 API 请求格式（POST 创建） */
function toCreatePayload(item) {
  const honorType = item.honorType ?? item.honor_type ?? 'text'
  const type = honorType === 'image' ? 1 : 0
  const atts = item.attachments ?? []
  const firstUrl = atts[0]?.url ?? atts[0] ?? item.imageUrl ?? item.file_url ?? ''
  const classId = item.classId ?? item.class_id ?? ''
  const payload = {
    class_id: typeof classId === 'number' ? classId : (parseInt(classId, 10) || classId),
    type,
    title: (item.title ?? '').toString().trim(),
    description: (item.description ?? '').toString().trim(),
    honor_date: toHonorDateStr(item.honorDate ?? item.honor_date)
  }
  if (type === 1 && firstUrl) payload.file_url = firstUrl
  return payload
}

/** 转为 API 请求格式（PUT 更新，仅 title/description/honor_date/update_by） */
function toUpdatePayload(item, rootState) {
  const payload = {
    title: (item.title ?? '').toString().trim(),
    description: (item.description ?? '').toString().trim(),
    honor_date: toHonorDateStr(item.honorDate ?? item.honor_date)
  }
  const uid = rootState?.currentUser?.id ?? rootState?.currentUser?.user_id ?? rootState?.currentUser?.userId
  if (uid != null) payload.update_by = String(uid)
  return payload
}

export default {
  namespaced: true,
  state: {
    list: [],
    loading: false,
    error: null
  },
  getters: {
    classHonors: state => state.list,
    loading: state => state.loading,
    error: state => state.error,
    byClassId: state => classId => {
      if (!classId) return state.list
      return state.list.filter(h => String(h.classId) === String(classId))
    }
  },
  mutations: {
    SET_LIST(state, list) {
      state.list = (list || []).map(normalizeHonor).filter(Boolean)
    },
    SET_LOADING(state, v) {
      state.loading = !!v
    },
    SET_ERROR(state, err) {
      state.error = err
    }
  },
  actions: {
    async fetchList({ commit, state }, params) {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      try {
        const res = await getClassHonors(params)
        const list = Array.isArray(res) ? res : (res.data ?? res.list ?? [])
        commit('SET_LIST', list)
        return list
      } catch (e) {
        commit('SET_ERROR', e.message)
        if (state.list.length === 0) {
          const mock = getMockHonors()
          commit('SET_LIST', mock)
          return mock
        }
        throw e
      } finally {
        commit('SET_LOADING', false)
      }
    },
    async fetchOne(_, id) {
      const res = await getClassHonor(id)
      const data = res.data ?? res
      return normalizeHonor(data)
    },
    async fetchByClass({ commit }, classId) {
      commit('SET_LOADING', true)
      commit('SET_ERROR', null)
      try {
        const res = await getClassHonorsByClass(classId)
        const list = Array.isArray(res) ? res : (res.data ?? res.list ?? res.records ?? res.items ?? [])
        commit('SET_LIST', Array.isArray(list) ? list : [])
        return list
      } catch (e) {
        commit('SET_ERROR', e.message)
        throw e
      } finally {
        commit('SET_LOADING', false)
      }
    },
    async create({ dispatch, commit, state, rootState }, payload) {
      const data = toCreatePayload(payload)
      try {
        await apiCreate(data)
        await dispatch('fetchList')
      } catch (e) {
        const list = (state.list && state.list.length) ? state.list : getMockHonors().map(normalizeHonor)
        const cls = rootState.classManage?.list?.find(c => String(c.id) === String(payload.classId)) || rootState.classes?.find(c => String(c.id) === String(payload.classId)) || { name: payload.className || '未知班级' }
        const newItem = normalizeHonor({
          honor_id: 'honor-' + Date.now(),
          type: (payload.honorType ?? payload.honor_type) === 'image' ? 1 : 0,
          class_id: payload.classId,
          class_info: { class_name: cls.name },
          title: payload.title,
          description: payload.description,
          honor_date: payload.honorDate || '',
          file_url: payload.attachments?.[0]?.url ?? payload.attachments?.[0]
        })
        commit('SET_LIST', [...list, newItem])
      }
    },
    async update({ dispatch, commit, state, rootState }, { id, data }) {
      try {
        const payload = toUpdatePayload(data, rootState)
        await apiUpdate(id, payload)
        await dispatch('fetchList')
      } catch (e) {
        const list = state.list.map(h => String(h.id) === String(id) ? { ...h, ...data } : h)
        commit('SET_LIST', list)
      }
    },
    async remove({ dispatch, commit, state }, id) {
      try {
        await apiDelete(id)
        await dispatch('fetchList')
      } catch (e) {
        commit('SET_LIST', state.list.filter(h => String(h.id) !== String(id)))
      }
    }
  }
}
